import {PolymerElement} from '@polymer/polymer';
import {ScopeBinding} from '../../behaviors/keli-binding-behavior';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';

class KeliLookupController extends ScopeBinding(PolymerElement) {
  static get is() {
    return 'keli-lookup-controller'
  }

  static get properties() {
    return {
      element: {type: Object},
      applyFilter: {type: Boolean, scopevar: true},
      applySort: {type: Boolean, scopevar: true},
      isOptionList: {type: Boolean, scopevar: true, observer: 'isOptionListChanged'},
      legacyData: {type: Array, scopevar: 'data', observer: 'legacyDataChanged'},
      manualKeliReady: {type: Boolean, value: true},
      optionList: {type: Object, scopevar: {options: {self: true}}},
      queryValue: {type: String, observer: 'queryValueChanged'},
      value: {type: String, scopevar: true, observer: 'valueChanged'}
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.searchingSymbol = Symbol('search is still running');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.element = null;
    this.searchingSymbol = null;
  }

  async elementReady(element) {
    this.element = element;

    this.keliname = this.element.keliname && this.element.keliname.split('.')[0];
    this.boundKeliRowId = element.boundKeliRowId;
    this.element.getKeliScopeRoot().addChildOfKeliScopeRoot(this);
    await this.keliReady(this.element.getKeliScope());

    if (this.element.data) {
      this.isOptionList = true;
      this.legacyData = this.element.data;
    }

    if (this.queryValue) {
      await this.executeSearch();
    }
  }

  isOptionListChanged(newValue) {
    this.element.stringMatching = !!newValue;
  }

  legacyDataChanged(newValue) {
    this.element.stringMatching = !!newValue;
  }

  queryValueChanged() {
    if (!this.isConnected) return;

    if (this.queryValue && (!this.element?.minimumToSearch || this.queryValue.length >= this.element.minimumToSearch)) {
      this.executeSearch();
    } else {
      this.element.loadingItems = false;
    }
  }

  valueChanged(newValue) {
    if (newValue && typeof newValue !== 'string') {
      return;
    }

    if (this.element) {
      this.element.hasValue = !!newValue;
      if (!this.element.fireKeliAccepted && !newValue && this.element) {
        this.queryValue = '';
        this.element.fieldInputOldValue = '';
        this.element.lastSelectedValue = '';
        this.element.currentValue = '';
      }
    }
  }

  async executeSearch() {
    if (!this.isConnected || !this.element) return;

    let result;
    if (this.isOptionList && !this.element.delay) {
      try {
        this.element.loadingItems = true;
        result = await this.loadItems();
      } finally {
        if (result !== this.searchingSymbol) {
          this.element.loadingItems = false;
          this.element.dispatchEvent(new CustomEvent('lookupItemsLoaded'));
        }
      }
    } else {
      this.element.loadingItems = true;
      this._executeSearchJob = Debouncer.debounce(this._executeSearchJob, timeOut.after(this.element.delay || 150), async () => {
        try {
          result = await this.loadItems();
        } finally {
          if (result !== this.searchingSymbol) {
            this.element.loadingItems = false;
            this.element.dispatchEvent(new CustomEvent('lookupItemsLoaded'));
          }
        }
      });
    }
  }

  sortResults(data, queryValue) {
    if (!data || !queryValue) {
      return data;
    }
    const exact = [];
    const startsWith = [];
    const others = [];
    for (let row of data) {
      if (!row) {
        continue;
      }

      const caption = ((typeof row === 'string' ? row : row.caption) || '').toLowerCase();
      if (queryValue.toLowerCase() === caption) {
        exact.push(row);
      } else if (caption.startsWith(queryValue.toLowerCase())) {
        startsWith.push(row);
      } else {
        others.push(row);
      }
    }
    return exact.concat(startsWith, others);
  }

  filterData(data, queryValue) {
    if (!data) {
      return undefined;
    }

    if (this.element.filterMode?.toLowerCase() === 'startswith') {
      return data.filter(item => {
        return (item?.caption || item)?.toLowerCase().startsWith(queryValue?.toLowerCase());
      });
    }

    const matcher = new Matchizzle(data, {
      keys: ['caption', 'searchField'.toLowerCase()],
      maximumItems: (queryValue ? this.element.maxChoices || 10 : undefined)
    });
    return matcher.search(queryValue);
  }

  async loadItems() {
    const requestedQueryValue = this.queryValue;
    let results;

    if (this.legacyData) {
      results = this.sortResults(this.legacyData, requestedQueryValue);
      results = this.filterData(results, requestedQueryValue);
    } else if (this.isOptionList) {
      const eventScope = new Scope(this.getKeliScope());
      eventScope.getEvent = () => ({
        input: requestedQueryValue
      });
      results = await this.optionList.call(eventScope.getBindingInstance());
      if (this.applySort !== false) {
        results = this.sortResults(results, requestedQueryValue);
      }
      if (this.applyFilter !== false) {
        results = this.filterData(results, requestedQueryValue);
      }
    } else if (requestedQueryValue) {
      const lookupResults = await this.runKeliFunction('doLookupSearch', requestedQueryValue, this.element.keliname, this.getKeliRowId());
      if (!this.isConnected) return;

      // Lookup results will be specifically null if superceded.
      if (lookupResults === null) {
        return this.searchingSymbol;
      }

      if (lookupResults) {
        this.element.insertable = lookupResults.insertable;
        results = lookupResults.rows;
      }
    }

    // The query has changed, throw the result and wait for the next one.
    if (this.queryValue !== requestedQueryValue) {
      if (this.queryValue) {
        return this.searchingSymbol;
      }
      return;
    }

    const lookupItems = [];
    if (results && results.length) {
      for (let i = 0; i < results.length; ++i) {
        if (typeof results[i] === 'string') {
          lookupItems.push({
            caption: results[i]
          });
        } else {
          lookupItems.push({
            rowid: results[i].id || undefined,
            caption: results[i].caption || undefined,
            hint: results[i].hint || undefined,
            icon: results[i].icon || undefined,
            action: results[i].action || undefined
          });
        }
      }
    }

    this.element.clearLookupItems();
    this.element.createLookupItems(lookupItems);
  }
}

customElements.define(KeliLookupController.is, KeliLookupController);
export {KeliLookupController};
